import type { CSSProperties, ReactNode } from "react";

// Lucide-Style Icons — Stroke 1.75, 24×24 viewBox, currentColor.
// Quelle: Scrolly Platform.html.

type IconProps = { size?: number; style?: CSSProperties };

const Icon = ({
  size = 20,
  children,
  style,
}: IconProps & { children: ReactNode }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
    aria-hidden="true"
  >
    {children}
  </svg>
);

export const IconHome = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 10.5 12 3l9 7.5V20a1.5 1.5 0 0 1-1.5 1.5h-4.5V14h-6v7.5H4.5A1.5 1.5 0 0 1 3 20z" />
  </Icon>
);

export const IconCompass = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="m15.5 8.5-2 5-5 2 2-5z" />
  </Icon>
);

export const IconFlame = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8.5 14.5a4 4 0 1 0 7 0c0-2-1-3.5-1.5-4.5-.5 1-1.5 1.5-2.5 1 .5-3-1-5-3-7-.5 3-3 4-3 7.5 0 2 1 3 3 3z" />
  </Icon>
);

export const IconBookmark = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z" />
  </Icon>
);

export const IconBell = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9z" />
    <path d="M10 21a2 2 0 0 0 4 0" />
  </Icon>
);

export const IconSearch = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Icon>
);

export const IconPlus = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

export const IconLibrary = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 3h4v18H3zM9 3h4v18H9zM15 5l4-1 2 17-4 1z" />
  </Icon>
);

export const IconMenu = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </Icon>
);

export const IconPlay = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 4v16l14-8z" fill="currentColor" />
  </Icon>
);

// Block 4 (FeaturedHero): IconStar, IconEye, IconClock, IconHeart.
// 1:1 aus Scrolly Platform.html:291/305/306/310 portiert.
// Star/Heart bekommen optionales `filled`-Prop, damit die Badge-/Hero-
// Glyphen als gefüllte Silhouette erscheinen.

type FillableIconProps = IconProps & { filled?: boolean };

export const IconStar = ({ filled, ...p }: FillableIconProps) => (
  <Icon {...p}>
    <path
      d="m12 3 2.7 5.8 6.3.9-4.5 4.4 1 6.3L12 17.5 6.5 20.4l1-6.3L3 9.7l6.3-.9z"
      fill={filled ? "currentColor" : "none"}
    />
  </Icon>
);

export const IconEye = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);

export const IconClock = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Icon>
);

export const IconHeart = ({ filled, ...p }: FillableIconProps) => (
  <Icon {...p}>
    <path
      d="M12 20s-7-4.5-9-8.5C1.5 8 4 4.5 7.5 5c2 .3 3.5 2 4.5 3.5C13 7 14.5 5.3 16.5 5 20 4.5 22.5 8 21 11.5c-2 4-9 8.5-9 8.5z"
      fill={filled ? "currentColor" : "none"}
    />
  </Icon>
);

export const IconScroll = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3v18m0 0-4-4m4 4 4-4M12 3l-4 4m4-4 4 4" />
  </Icon>
);

export const IconX = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Icon>
);

export const IconShare = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <path d="M8.6 13.5 15.4 17.5M15.4 6.5 8.6 10.5" />
  </Icon>
);

export const IconMoreHorizontal = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="1.25" />
    <circle cx="5" cy="12" r="1.25" />
    <circle cx="19" cy="12" r="1.25" />
  </Icon>
);

// Viewer-Mode-Toggle: Expand = Phone → Immersive, Contract = Immersive → Phone.
// Beide kennen kein `filled`-Prop, weil sie rein als Chrome-Icons leben.

export const IconMaximize = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 9V5a1 1 0 0 1 1-1h4" />
    <path d="M20 9V5a1 1 0 0 0-1-1h-4" />
    <path d="M4 15v4a1 1 0 0 0 1 1h4" />
    <path d="M20 15v4a1 1 0 0 1-1 1h-4" />
  </Icon>
);

export const IconMinimize = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9 4v4a1 1 0 0 1-1 1H4" />
    <path d="M15 4v4a1 1 0 0 0 1 1h4" />
    <path d="M9 20v-4a1 1 0 0 0-1-1H4" />
    <path d="M15 20v-4a1 1 0 0 1 1-1h4" />
  </Icon>
);

export const IconCheckBadge = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    style={{ flexShrink: 0 }}
    aria-hidden="true"
  >
    <path
      d="M12 1l2.2 2.4 3.3-.4.4 3.3L20.4 8.6 18 11l2.4 2.4-2.5 2.2.4 3.3-3.3.4L12 21.8l-2.4-2.4-3.3.4-.4-3.3L3.6 14.4 6 12 3.6 9.6 6.1 7.4l-.4-3.3 3.3-.4z"
      fill="#FF3366"
    />
    <path
      d="m8 12 3 3 5-6"
      stroke="#fff"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
